// src/plugins/route-top-progress.js
import { defineComponent, h, createApp, reactive } from 'vue'

export default {
  install(app, options = {}) {
    const router = options.router
    if (!router) {
      throw new Error('[RouteTopProgress] Missing required option: { router }')
    }

    const cfg = {
      height: options.height || 3,            // px
      zIndex: options.zIndex || 9999,
      color: options.color || null,           // if null, uses default gradient
      gradient: options.gradient || 'linear-gradient(90deg, #42b983, #2c8f6b)',
      minDuration: options.minDuration ?? 150, // ms (avoid flicker)
      trickleInterval: options.trickleInterval ?? 200, // ms
      trickleMax: options.trickleMax ?? 90,   // %
      ...options,
    }

    // --- singleton state ---
    let barEl = null
    let timer = null
    let visibleSince = 0
    let navigating = false
    const state = reactive({ progress: 0, visible: false })

    function getStyleString() {
      return `position:fixed;top:-1px;left:0;width:${state.progress}%;height:${cfg.height}px;z-index:${cfg.zIndex};opacity:${state.visible ? 1 : 0};pointer-events:none;transition:width 0.2s ease, opacity 0.25s ease;will-change:width;background:${cfg.color ? cfg.color : cfg.gradient};`
    }

    function ensureMounted() {
      if (barEl) return
      barEl = document.createElement('div')
      barEl.setAttribute('style', getStyleString())
      document.body.appendChild(barEl)
    }

    function updateBar() {
      if (barEl) {
        barEl.setAttribute('style', getStyleString())
      }
    }

    function clearTrickle() {
      if (timer) clearInterval(timer)
      timer = null
    }

    function start() {
      ensureMounted()
      if (navigating) return
      navigating = true

      visibleSince = Date.now()
      state.visible = true
      state.progress = Math.max(state.progress, 5)
      updateBar()

      clearTrickle()
      timer = setInterval(() => {
        if (!state.visible) return
        if (state.progress < cfg.trickleMax) {
          state.progress += Math.random() * 8
          updateBar()
        }
      }, cfg.trickleInterval)
    }

    function finish() {
      if (!barEl) return
      const elapsed = Date.now() - visibleSince
      const delay = Math.max(0, cfg.minDuration - elapsed)

      // Only finish if we actually started a nav
      if (!navigating) return

      window.setTimeout(() => {
        clearTrickle()
        state.progress = 100
        updateBar()

        // allow the 100% transition to render
        window.setTimeout(() => {
          state.visible = false
          state.progress = 0
          navigating = false
          updateBar()
        }, 250)
      }, delay)
    }

    // Expose helpers for manual control
    app.config.globalProperties.$routeProgress = {
      start,
      finish,
    }

    // --- Router hooks ---
    router.beforeEach((to, from) => {
      start()
    })

    // Fallback: ensure we finish even if mount detection fails
    router.afterEach(() => {
      // Wait a frame or two so fast navigations don't instantly complete
      requestAnimationFrame(() => requestAnimationFrame(() => finish()))
    })

    router.onError(() => {
      finish()
    })
  },
}
